import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../../components/Button';
import LoadingButton from '../../components/LoadingButton/LoadingButton';
import LetterWritingBlock from '../../components/LetterWritingBlock';
import TinselHeader from '../../components/TinselHeader/TinselHeader';
import { useUIText, useAppConstants } from '../../hooks/useConfig';
import { Typo } from '../../utils/typo';
import './Letter.scss';

import apiClient from '../../api/apiClient';
import { showTelegramAlert, triggerNotification } from '../../utils/telegramActions';
import { useAnalytics } from '../../hooks/useAnalytics';

function Letter() {
    const navigate = useNavigate();
    const UI_TEXT = useUIText();
    const APP_CONFIG = useAppConstants();
    const { trackEvent } = useAnalytics();
    const [field1, setField1] = useState('');
    const [field2, setField2] = useState('');
    const [field3, setField3] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [startedTyping, setStartedTyping] = useState({});
    const [isInputFocused, setIsInputFocused] = useState(false);

    const minLengths = APP_CONFIG.LETTER?.MIN_LENGTHS || { FIELD_1: 40, FIELD_2: 40, FIELD_3: 1 };
    const isFormValid = field1.trim().length >= minLengths.FIELD_1 &&
        field2.trim().length >= minLengths.FIELD_2 &&
        field3.trim().length >= minLengths.FIELD_3;

    useEffect(() => {
        trackEvent('view_letter');
    }, [trackEvent]);

    const handleInputChange = (setter, fieldName) => (value) => {
        setter(value);
        if (!startedTyping[fieldName] && value.length > 0) {
            setStartedTyping(prev => ({ ...prev, [fieldName]: true }));
            trackEvent(`letter_input_start_${fieldName}`);
        }
    };

    const handleInputFocus = () => {
        setIsInputFocused(true);
    };

    const handleInputBlur = () => {
        setIsInputFocused(false);
    };

    const handleSendLetter = async () => {
        if (!isFormValid) return;

        trackEvent('click_send_letter');
        setIsLoading(true);
        try {
            await apiClient.sendLetter({ field1, field2, field3 });
            triggerNotification('success');
            trackEvent('letter_sent_success');
            navigate('/result');
        } catch (error) {
            trackEvent('letter_sent_error', { error: error.message });
            // Отображаем сообщение об ошибке от сервера (оно уже на русском)
            showTelegramAlert(error.message || UI_TEXT.ERRORS?.DEFAULT || 'Произошла ошибка при отправке письма.');
            console.error('Ошибка отправки письма:', error);
        } finally {
            setIsLoading(false);
        }
    };



    return (
        <div className="letter-container">
            <TinselHeader />
            <div className="letter-content">
                <div className="letter-main-text">
                    <div className="text-line">
                        <Typo>{UI_TEXT.LETTER?.TITLE_LINE_1}</Typo>
                    </div>
                    <div className="text-line">
                        <Typo>{UI_TEXT.LETTER?.TITLE_LINE_2}</Typo>
                    </div>
                </div>
                <LetterWritingBlock
                    field1={field1}
                    field2={field2}
                    field3={field3}
                    onField1Change={handleInputChange(setField1, 'field_1')}
                    onField2Change={handleInputChange(setField2, 'field_2')}
                    onField3Change={handleInputChange(setField3, 'field_3')}
                    onInputFocus={handleInputFocus}
                    onInputBlur={handleInputBlur}
                />
            </div>
            <div className={`letter-gradient-overlay ${isInputFocused ? 'hide-footer' : ''}`}></div>
            <div className={`letter-footer ${isInputFocused ? 'hide-footer' : ''}`}>
                {isLoading ? (
                    <LoadingButton className="send-letter-btn" />
                ) : (
                    <Button
                        className="send-letter-btn"
                        onClick={handleSendLetter}
                        disabled={!isFormValid}
                        active={isFormValid}
                    >
                        {UI_TEXT.LETTER?.BUTTON}
                    </Button>
                )}
            </div>
        </div>
    );
}

export default Letter;
